"use client";

import React, { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SectionLanguage = "en" | "it";
const MAP_URL = "https://www.google.com/maps/d/u/0/embed?mid=1_wiUN-jSQpprZJnKfWLeNq_aCLa9Yk8&ehbc=2E312F";
const events = {
  en: [{
    time: "4:00 PM",
    location: "CHIESA DI SANTO SPIRITO IN SASSIA",
    address: "Via dei Penitenzieri 12, 00193 Roma",
    title: "The Ceremony",
    description: "Join us for a Catholic ceremony in one of Rome's beautiful churches, just steps from St. Peter's."
  }, {
    time: "6:00 PM",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "Aperitivo",
    description: "Celebrate the start of the evening with prosecco, wine, and light bites at sunset."
  }, {
    time: "8:00 PM",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "Dinner",
    description: "Join us for a seated dinner under the Roman sky."
  }, {
    time: "To Follow",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "After Party",
    description: "Let the celebration continue with music, cocktails, and dancing into the night."
  }],
  it: [{
    time: "16:00",
    location: "CHIESA DI SANTO SPIRITO IN SASSIA",
    address: "Via dei Penitenzieri 12, 00193 Roma",
    title: "La Cerimonia",
    description: "Unitevi a noi per la cerimonia cattolica in una delle bellissime chiese di Roma, a pochi passi da San Pietro."
  }, {
    time: "18:00",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "Aperitivo",
    description: "Festeggiamo l'inizio della serata con prosecco, vino e stuzzichini al tramonto."
  }, {
    time: "20:00",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "Cena",
    description: "Unitevi a noi per una cena servita sotto il cielo di Roma."
  }, {
    time: "A Seguire",
    location: "ROME CAVALIERI WALDORF ASTORIA",
    address: "Via Alberto Cadlolo 101, 00136 Roma",
    title: "After Party",
    description: "Continuiamo a festeggiare con musica, cocktail e balli fino a tarda notte."
  }]
};
export default function TheCelebrationItinerary() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const [language, setLanguage] = useState<SectionLanguage>("en");
  useEffect(() => {
    const savedLanguage = localStorage.getItem("site-language");
    if (savedLanguage === "en" || savedLanguage === "it") {
      setLanguage(savedLanguage);
    }
  }, []);
  const t = {
    en: {
      heading: "Wedding Day Itinerary",
      date: "Friday, September 11, 2026",
      mapHeading: "Map of the Day",
      mapIntro: "The map below shows the ceremony and reception locations."
    },
    it: {
      heading: "Il Programma del Matrimonio",
      date: "Venerdì, 11 Settembre 2026",
      mapHeading: "Mappa della Giornata",
      mapIntro: "La mappa mostra la location della cerimonia e del ricevimento."
    }
  }[language];
  const content = events[language];
  return <section id="the-celebration-itinerary" className="bg-background py-16 md:py-24 text-foreground overflow-hidden">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {/* HEADER */}
        <div className="mb-12 md:mb-20 text-center">
          <h2 className="mb-4 font-serif text-3xl sm:text-4xl md:text-5xl">
            {t.heading}
          </h2>

          <p className="mx-auto max-w-2xl text-sm sm:text-base text-muted-foreground">
            {t.intro}
          </p>

          <p className="mt-6 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-muted-foreground">
            {t.date}
          </p>
        </div>

        {/* TIMELINE */}
        <div ref={ref} className="relative">
          <div className="absolute left-5 md:left-1/2 -translate-x-1/2 w-px h-full bg-border" />

          <div className="flex flex-col gap-10 md:gap-24">
            {content.map((event, i) => <div key={i} className={`relative flex flex-col md:flex-row transition-all ${isVisible ? "opacity-100" : "opacity-0"}`}>
                <div className="w-full md:w-1/2 md:text-right md:pr-12 pl-14 md:pl-0">
                  <div className="text-primary text-xl font-serif">
                    {event.time}
                  </div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">
                    {event.location}
                  </div>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(event.address)}`} target="_blank" className="text-xs text-muted-foreground hover:text-primary">
                    {event.address}
                  </a>
                </div>

                <div className="absolute left-5 md:left-1/2 -translate-x-1/2 bg-background border rounded-full p-2">
                  <Landmark />
                </div>

                <div className="w-full md:w-1/2 pl-14 md:pl-12">
                  <h3 className="text-2xl font-serif">{event.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {event.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* MAP SECTION */}
        <div className="mt-20 text-center">
          <h3 className="text-3xl font-serif mb-3">{t.mapHeading}</h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-2xl mx-auto">
            {t.mapIntro}
          </p>

          <div className="mx-auto w-full max-w-4xl rounded-2xl overflow-hidden border shadow-sm">
            <div className="relative w-full aspect-[4/5] sm:aspect-[16/10] md:aspect-[16/9]">
              <iframe src={MAP_URL} className="absolute inset-0 w-full h-full border-0" loading="lazy" allowFullScreen />
            </div>
          </div>
        </div>
      </div>
    </section>;
}